import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'

import { ApiCaller } from '@graphit/callers'
import type { Workspace } from '@graphit/types'

export const workspacesQueryOptions = queryOptions({
	queryKey: ['workspaces'],
	queryFn: ApiCaller.getWorkspaces
})

export const useWorkspacesQuery = () => useSuspenseQuery(workspacesQueryOptions)

export const useCreateWorkspaceMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ApiCaller.createWorkspace,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['workspaces'] })
		}
	})
}

export const useUpdateWorkspaceMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ({ id, workspace }: { id: string; workspace: Partial<Workspace> }) =>
			ApiCaller.updateWorkspace(id, workspace),
		onSuccess: (_, variables) => {
			queryClient.invalidateQueries({ queryKey: ['workspaces'] })
			queryClient.invalidateQueries({ queryKey: ['workspace', variables.id] })
		}
	})
}

export const useDeleteWorkspaceMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ApiCaller.deleteWorkspace,
		onSuccess: (_, workspaceId) => {
			queryClient.setQueryData(workspacesQueryOptions.queryKey, old => old?.filter(w => w.id !== workspaceId))
			queryClient.removeQueries({ queryKey: ['workspace', workspaceId] })
			queryClient.invalidateQueries({ queryKey: ['relationships'] })
			queryClient.invalidateQueries({ queryKey: ['types', 'stats'] })
			queryClient.invalidateQueries({ queryKey: ['cartographies'] })
			queryClient.removeQueries({ queryKey: ['cartography'] })
		}
	})
}

export const useDuplicateWorkspaceMutation = () => {
	const queryClient = useQueryClient()
	return useMutation({
		mutationFn: ApiCaller.duplicateWorkspace,
		onSuccess: workspace => {
			queryClient.invalidateQueries({ queryKey: ['workspaces'] })
			queryClient.invalidateQueries({ queryKey: ['workspace', workspace.id] })
			queryClient.invalidateQueries({ queryKey: ['relationships'] })
			queryClient.invalidateQueries({ queryKey: ['types', 'stats'] })
			queryClient.invalidateQueries({ queryKey: ['cartographies'] })
			queryClient.invalidateQueries({ queryKey: ['cartography'] })
		}
	})
}
